import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { Context } from '../../../context/UserContext';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';

import Form from './Form';
import styles from '../../form/Form.module.css';

import * as DataListAPI from '../../../api/dataList';
import * as EmpresaAPI from '../../../api/empresa';
import * as FinanceiroAPI from '../../../api/financeiro';
import * as LancamentosAPI from '../../../api/lancamentos';

import { retornaData } from '../../../utils/retornaDataFormatada';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';

import { bootLancamento } from './boot/bootLancamento';
import { normalizeLancamentoPayload } from './helpers/normalizeLancamento';
import { useLancamentoModule } from './modules/useLancamentoModule';

const CreateOrEdit = () => {
    const [token] = useState(localStorage.getItem('token') || '');
    const { parametro } = useContext(Context);

    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);

    const pathname = '/Lancamentos';
    const ul = urlParams.get('ul');
    const li = urlParams.get('li');
    const tipo = urlParams.get('tipo');
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const btnTextUrl = urlParams.get('btnText');
    const origem = urlParams.get('origem');

    // 🔒 FIX 1: btnText protegido (não deixa baixar em edição)
    const isBaixar = btnTextUrl === 'Baixar';
    const isEditar = !!uuid && !isBaixar;
    const btnText = isBaixar ? 'Baixar' : isEditar ? 'Atualizar' : 'Cadastrar';

    const params = new URLSearchParams({
        ul,
        li,
        tipo,
        operacao: operacaoUUID,
        uuid,
        btnText,
        origem,
    });

    const [dados, setDados] = useState(() => bootLancamento({ formData: {}, tipo }));

    const [pessoa, setPessoa] = useState({});
    const [centroCusto, setCentroCusto] = useState({});
    const [subCentroCusto, setSubCentroCusto] = useState({});
    const [lancamentoBaixa, setLancamentoBaixa] = useState({});
    const [mapaAbastecimento, setMapaAbastecimento] = useState({});

    const [optionsCentroCustos, setCC] = useState([]);
    const [optionsForma, setForma] = useState([]);
    const [optionsEmpresa, setEmpresa] = useState([]);

    const { empresaId, empresaIds } = getEmpresaScope();

    const consultaEmpresa = async () => {
        const result = await EmpresaAPI.empresa(empresaId);
        setEmpresa(result);
    };

    const consultaForma = async (obj) => {
        const result = await FinanceiroAPI.buscaForma(obj);
        setForma(result || []);
    };

    const consultaCC = async (empresaIdConsulta = dados.empresaId) => {
        if (!empresaIdConsulta) return;

        const result = await DataListAPI.dataList({
            empresaId: empresaIdConsulta,
            tabela: 'centroCusto',
            id: 'id',
            descricao: 'descricao',
            condicao: tipo,
        });

        setCC(result || []);
    };

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
        }
    }, [token, navigate]);

    useEffect(() => {
        if (!uuid) {
            const boot = bootLancamento({
                formData: {
                    empresaId,
                    parcela: 1,
                    dataLancamento: retornaData(),
                    dataVencimento: retornaData(),
                    tipo,
                },
                tipo,
            });

            setDados(boot);
            setPessoa({});
            setCentroCusto({});
            setSubCentroCusto({});
            setLancamentoBaixa({});
            setMapaAbastecimento({});
        }
    }, [uuid, tipo, empresaId]);

    useEffect(() => {
        consultaEmpresa();
    }, []);

    useEffect(() => {
        if (!dados?.empresaId) return;

        consultaForma({ tipo, empresaId: dados.empresaId });
        consultaCC(dados.empresaId);
    }, [dados?.empresaId, tipo]);

    useEffect(() => {
        if (!uuid || uuid === 'null') return;

        LancamentosAPI.consulta(uuid)
            .then((response) => {
                const registro = response.data || {};
                const boot = bootLancamento({ formData: registro, tipo });

                setDados(boot);
                setPessoa(registro?.Pessoa || {});
                setCentroCusto(registro?.centroCusto || {});
                setSubCentroCusto(registro?.SubCentroCusto || {});
                setLancamentoBaixa(registro?.lancamentoBaixa || {});
                setMapaAbastecimento(registro?.mapaAbastecimento || {});
            })
            .catch((error) => {
                console.error(error);
                setFlashMessage('Erro ao carregar lançamento.', 'error');
            });
    }, [uuid, tipo]);

    // 🔒 FIX 2: NÃO calcular baixa em edição
    useEffect(() => {
        if (btnText !== 'Cadastrar') return;

        const precisaCalcular =
            (!dados?.baixa && dados?.valor) || dados?.acrescimoValor || dados?.valorBaixado || dados?.descontoValor;

        if (!precisaCalcular) return;

        const baixa = (
            Number(dados?.valor || 0) +
            Number(dados?.acrescimoValor || 0) -
            Number(dados?.valorBaixado || 0) -
            Number(dados?.descontoValor || 0)
        ).toFixed(2);

        if (String(dados?.baixa) === String(baixa)) return;

        setDados((prev) => ({
            ...prev,
            baixa,
        }));
    }, [btnText, dados]);

    // 🔒 FIX 3: baixa automática só no modo baixar
    useEffect(() => {
        if (btnText !== 'Baixar') return;
        if (!dados?.valorAberto) return;
        if (dados?.baixa) return;

        setDados((prev) => ({
            ...prev,
            baixa: prev.valorAberto,
            dataBaixa: prev.dataBaixa || retornaData(),
        }));
    }, [btnText, dados]);

    async function managerLancamento(payload) {
        try {
            const dadosNormalizados = normalizeLancamentoPayload(payload);

            let response;

            // 🔒 FIX 4: ordem segura (não baixa por engano)
            if (btnText === 'Baixar') {
                response = await LancamentosAPI.baixar(dadosNormalizados);
            } else if (uuid) {
                response = await LancamentosAPI.editar(dadosNormalizados);
            } else {
                response = await LancamentosAPI.cadastrar(dadosNormalizados);
            }

            setFlashMessage(response.data.message, response.data.type);

            if (response.data.type === 'success') {
                setTimeout(() => navigate(pathname, params), parametro.msgTempo * 1000);
            }
        } catch (error) {
            console.error(error);
            setFlashMessage('Erro ao salvar lançamento.', 'error');
        }
    }

    const lancamentoModule = useLancamentoModule({
        token,
        tipo,
        btnText,
        empresaIds,
        parametro,
        setFlashMessage,
        dados,
        setDados,
        pessoa,
        setPessoa,
        centroCusto,
        setCentroCusto,
        subCentroCusto,
        setSubCentroCusto,
        lancamentoBaixa,
        setLancamentoBaixa,
        mapaAbastecimento,
        setMapaAbastecimento,
        optionsCentroCustos,
        handleSubmit: managerLancamento,
    });

    return (
        <section className={`${styles.form_container} ${styles[parametro.tema]}`}>
            <Form
                dados={dados}
                pessoa={pessoa}
                parametro={parametro}
                centroCusto={centroCusto}
                subCentroCusto={subCentroCusto}
                lancamentoBaixa={lancamentoBaixa}
                mapaAbastecimento={mapaAbastecimento}
                optionsEmpresa={optionsEmpresa}
                optionsForma={optionsForma}
                caminhoVoltar={pathname}
                btnText={btnText}
                {...lancamentoModule}
            />
        </section>
    );
};

export default CreateOrEdit;
